import { useState } from 'react';
import {
  Button, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger,
  MessageBar, MessageBarActions, MessageBarBody, Text, makeStyles, tokens,
} from '@coreai/fluentui-react';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useAzureResourcePicker } from '@/hooks/useAzureResourcePicker';
import type { AzurePickerTarget, AzureResourcePickerOptions } from '@/types/azureResourcePicker';
import { isAzurePickerTargetAllowed, parseAzurePickerTarget } from '@/utils/azureResourcePicker';
import { AzurePickerSelect, pickerItems, retryPickerQuery, type PickerQuery } from './AzurePickerQueryControls';

const useStyles = makeStyles({
  surface: { width: '760px', maxWidth: 'calc(100vw - 32px)' },
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  secondary: { color: tokens.colorNeutralForeground2 },
  selection: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, overflowWrap: 'anywhere',
    padding: tokens.spacingHorizontalM, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium,
  },
});

interface AzureResourcePickerDialogProps {
  initialValue: string;
  options: AzureResourcePickerOptions;
  onSelect: (id: string) => void;
}

export function AzureResourcePickerDialog({ initialValue, options, onSelect }: AzureResourcePickerDialogProps) {
  const styles = useStyles();
  const [initial] = useState(() => parseAzurePickerTarget(initialValue));
  const [subscription, setSubscription] = useState<AzurePickerTarget | undefined>(() => initial ? {
    id: initial.id.split('/').slice(0, 3).join('/'), name: initial.subscriptionId,
    subscriptionId: initial.subscriptionId, kind: 'subscription',
  } : undefined);
  const [group, setGroup] = useState<AzurePickerTarget | undefined>(() => initial?.resourceGroup ? {
    id: initial.id.split('/').slice(0, 5).join('/'), name: initial.resourceGroup,
    resourceGroup: initial.resourceGroup, subscriptionId: initial.subscriptionId, kind: 'resourceGroup',
  } : undefined);
  const [resource, setResource] = useState<AzurePickerTarget | undefined>(initial?.kind === 'resource' ? initial : undefined);
  const [resourceType, setResourceType] = useState(initial?.type ?? '');
  const resourcesEnabled = options.allowedTargetKinds?.includes('resource') ?? true;
  const queries = useAzureResourcePicker({
    subscriptionId: subscription?.subscriptionId ?? '', resourceGroup: group?.name,
    resourceType, allowedResourceTypes: options.allowedResourceTypes,
  }, resourcesEnabled);
  const subscriptions = pickerItems(queries.subscriptions);
  const groups = pickerItems(queries.groups);
  const resources = [...new Map(pickerItems(queries.resources).map((item) => [item.id.toLowerCase(), item])).values()];
  const selected = resource ?? group ?? subscription;
  const canSelect = isAzurePickerTargetAllowed(selected, options);
  const failures: { label: string; query: PickerQuery<unknown> }[] = [
    { label: 'subscriptions', query: queries.subscriptions },
    { label: 'resource groups', query: queries.groups },
    { label: 'resource types', query: queries.types },
    { label: 'resources', query: queries.resources },
  ].filter((entry) => entry.query.error);
  const canBrowseTypes = resourcesEnabled && !!subscription && !!group;
  const canBrowseResources = canBrowseTypes && !!resourceType;
  const resetResource = () => { setResource(undefined); setResourceType(''); };

  return <DialogSurface className={styles.surface} data-testid="azure-resource-picker-dialog"
    onKeyDown={(event) => { if (event.key === 'Escape') event.stopPropagation(); }}>
    <DialogBody>
      <DialogTitle action={<DialogTrigger disableButtonEnhancement>
        <Button appearance="subtle" aria-label="Close resource picker" icon={<Dismiss24Regular />} />
      </DialogTrigger>}>Select an Azure resource</DialogTitle>
      <DialogContent className={styles.content}>
        {failures.map(({ label, query }) => (
          <MessageBar key={label} intent="warning" role="alert">
            <MessageBarBody>{query.error?.message}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="subtle" disabled={query.isFetching}
                onClick={() => retryPickerQuery(query)}>Retry {label}</Button>
            </MessageBarActions>
          </MessageBar>
        ))}
        <Text className={styles.secondary}>Browse with your signed-in Azure account. Workflow connection permissions may differ.</Text>
        <AzurePickerSelect label="Subscription" itemsLabel="subscriptions" query={queries.subscriptions}
          options={subscriptions.map((item) => ({ value: item.id, text: item.name, secondaryText: item.subscriptionId }))}
          value={subscription?.id} placeholder="Select a subscription"
          onChange={(id) => {
            setSubscription(subscriptions.find((item) => item.id === id)); setGroup(undefined); resetResource();
          }} />
        <AzurePickerSelect label="Resource group" itemsLabel="resource groups" query={queries.groups}
          options={[{ value: '', text: '(none)' }, ...groups.map((item) => ({ value: item.id, text: item.name }))]}
          value={group?.id} placeholder="Select a resource group" disabled={!subscription}
          onChange={(id) => { setGroup(groups.find((item) => item.id === id)); resetResource(); }} />
        {resourcesEnabled && <>
          <AzurePickerSelect label="Resource type" itemsLabel="resource types" query={queries.types}
            options={[{ value: '', text: '(none)' }, ...pickerItems(queries.types).map((item) => ({
              value: item.type, text: item.type, secondaryText: `${item.count} resources`,
            }))]}
            value={resourceType || undefined} placeholder="Select a resource type" disabled={!canBrowseTypes}
            onChange={(type) => { setResourceType(type); setResource(undefined); }} />
          <AzurePickerSelect label="Resource" itemsLabel="resources" query={queries.resources}
            options={[{ value: '', text: '(none)' }, ...resources.map((item) => ({ value: item.id, text: item.name, secondaryText: item.id, searchTerms: [item.location ?? ''] }))]}
            value={resource?.id} placeholder="Search and select a resource" disabled={!canBrowseResources}
            fallbackDisplayValue={resource?.name} noMatchMessage="No matching resources found"
            onChange={(id) => setResource(resources.find((item) => item.id === id))} />
        </>}
        <div className={styles.selection} role="status" aria-live="polite" aria-atomic="true" data-testid="azure-resource-picker-selection">
          <Text weight="semibold">Selected resource</Text>
          <Text size={200}>{selected ? selected.id : 'None'}</Text>
          {selected && !canSelect && <Text>Select an eligible target for this parameter.</Text>}
        </div>
      </DialogContent>
      <DialogActions>
        <DialogTrigger disableButtonEnhancement><Button>Cancel</Button></DialogTrigger>
        <Button appearance="primary" disabled={!canSelect} onClick={() => { if (selected && canSelect) onSelect(selected.id); }}>OK</Button>
      </DialogActions>
    </DialogBody>
  </DialogSurface>;
}